/**
 * Client-side Excel (.xlsx) generator using SheetJS (xlsx).
 * Multiple sheets, visible formulas.
 */
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function aoaToSheet(rows, { colWidths } = {}) {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  if (colWidths) ws["!cols"] = colWidths.map((w) => ({ wch: w }));
  return ws;
}

function buildBudgetSheet(analysis) {
  const r = analysis?.results || {};
  const summary = r.summary || {};
  const lines = r.aggregated_budget_lines || [];
  const rows = [
    ["VITAE-PUBLICA — Budget agrégé"],
    [],
    ["Devise", summary.currency || ""],
    ["Total recettes agrégées", summary.total_revenue_aggregated || 0],
    ["Total dépenses agrégées", summary.total_expenditure_aggregated || 0],
    ["Total dette agrégée", summary.total_debt_aggregated || 0],
    [],
    ["Secteur", "Montant", "%", "Jours de vie (=Montant/(Salaire/30))"],
  ];
  const salaireJ =
    r.national?.salaire_journalier_moyen ||
    r.profiles?.[0]?.individual?.salaire_journalier ||
    0;
  lines.forEach((bl, i) => {
    const rowIdx = 9 + i; // 1-based (Excel)
    rows.push([
      bl.sector || "—",
      bl.amount || 0,
      bl.percentage || 0,
      // Excel formula: divide column B by (salary/30). We store salaryJ as a literal.
      salaireJ > 0 ? { f: `B${rowIdx}/${salaireJ}` } : 0,
    ]);
  });
  return aoaToSheet(rows, { colWidths: [36, 18, 10, 24] });
}

function buildVitalistSheet(analysis) {
  const r = analysis?.results || {};
  const profiles = r.profiles || [];
  const rows = [
    ["VITAE-PUBLICA — Calculs vitalistes par profil"],
    [],
    [
      "Profil",
      "Salaire mensuel",
      "Salaire journalier (=B/30)",
      "Total prélèvements/an",
      "Jours de vie sacrifiés / an (=D/C)",
      "Mois de vie sacrifiés",
      "% vie fiscale",
      "Années carrière sacrifiées",
    ],
  ];
  profiles.forEach((p, i) => {
    const ind = p.individual || {};
    const rowIdx = 4 + i;
    const monthly = ind.salaire_mensuel || 0;
    rows.push([
      p.profile_name || "—",
      monthly,
      monthly > 0 ? { f: `B${rowIdx}/30` } : 0,
      ind.total_prelevements || 0,
      ind.salaire_journalier > 0
        ? { f: `D${rowIdx}/C${rowIdx}` }
        : 0,
      ind.mois_vie_sacrifies || 0,
      ind.ratio_vie_fiscale_pct || 0,
      ind.annees_vie_sur_carriere || 0,
    ]);
  });
  rows.push([]);
  rows.push(["Formule vitaliste canonique : Jours_de_vie = Prélèvements / (Salaire_mensuel / 30)"]);
  return aoaToSheet(rows, {
    colWidths: [28, 18, 22, 20, 28, 22, 16, 26],
  });
}

function buildLegalSheet(analysis) {
  const r = analysis?.results || {};
  const legal = r.legal_analysis || {};
  const infractions = legal.infractions || [];
  const rows = [
    ["VITAE-PUBLICA — Analyse juridique"],
    [],
    ["Synthèse :", legal.synthese || ""],
    [
      "Confiance modèle :",
      legal.analysis_confidence != null
        ? `${Math.round(legal.analysis_confidence * 100)}%`
        : "",
    ],
    [],
    [
      "Qualification",
      "Description",
      "Montant (UM)",
      "Jours de vie",
      "Niveau gravité",
      "Articles",
    ],
  ];
  infractions.forEach((inf) => {
    rows.push([
      inf.qualification || "",
      inf.description || "",
      inf.montant_um ?? "",
      inf.jours_de_vie ?? "",
      inf.niveau_gravite ?? "",
      (inf.articles_applicables || [])
        .map((a) => `${a.article} — ${a.texte_titre}`)
        .join(" · "),
    ]);
  });
  rows.push([]);
  rows.push(["Responsabilités par catégorie"]);
  ["professionnelle", "civile", "administrative", "penale"].forEach((k) => {
    const items = legal.responsabilites?.[k] || [];
    rows.push([k.toUpperCase()]);
    items.forEach((it) => {
      rows.push([
        "",
        it.description || "",
        "",
        "",
        "",
        (it.articles || []).join(" · "),
      ]);
    });
  });
  return aoaToSheet(rows, { colWidths: [28, 44, 16, 14, 14, 44] });
}

function buildRawSheet(analysis) {
  const rows = [
    ["VITAE-PUBLICA — Données brutes (JSON dump)"],
    [],
    ["Champ", "Valeur"],
    ["analysis_id", analysis?.id || ""],
    ["project_id", analysis?.project_id || ""],
    ["created_at", analysis?.created_at || ""],
    [],
    ["Payload JSON complet :"],
    [JSON.stringify(analysis?.results || {}, null, 2)],
  ];
  return aoaToSheet(rows, { colWidths: [32, 120] });
}

export async function generateExcel({ analysis, filename }) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildBudgetSheet(analysis), "Budget");
  XLSX.utils.book_append_sheet(
    wb,
    buildVitalistSheet(analysis),
    "Calculs Vitalistes"
  );
  XLSX.utils.book_append_sheet(
    wb,
    buildLegalSheet(analysis),
    "Analyse Juridique"
  );
  XLSX.utils.book_append_sheet(wb, buildRawSheet(analysis), "Données Brutes");

  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  const blob = new Blob([out], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename);
  return blob.size;
}
